import React, { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import styled from "styled-components";
import { MdPictureAsPdf } from "react-icons/md";

interface ImagePdfDropzoneProps {
  onFileSelected: (selected: File | null) => void;
  base64: string;
  width?: number;
  height?: number;
  uploadMessage?: string;
}

const DropContainer = styled.div<{ isDragActive: boolean; isDragReject: boolean }>`
  border: 2px dashed #cccccc;
  border-radius: 4px;
  padding: 40px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: ${(props) =>
    props.isDragActive ? "#e0f7fd" : props.isDragReject ? "#ffd7d7" : "white"};
  transition: all 0.3s ease;
  &:hover {
    border-color: #3884cc;
    background-color: #f9f9f9;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const UploadedImage = styled.img<{ width?: number; height?: number }>`
  width: ${({ width }) => (width ? `${width}px` : 'auto')};
  height: ${({ height }) => (height ? `${height}px` : 'auto')};
  object-fit: cover;
`;

const PdfIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  text-align: center;
  width: 100%;
`;

const PdfIcon = styled.div`
  font-size: 120px;
  color: #dc2626;
  opacity: 0.8;
  transition: transform 0.3s ease, opacity 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    opacity: 1;
  }
`;

const ChangeImageButton = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  background-color: #fff;
  border: none;
  padding: 5px;
  cursor: pointer;
`;

const UploadMessage = styled.p`
  text-align: center;
  font-size: 16px;
  color: #999999;
  margin: 0;
  &:hover {
    color: #3884cc;
  }
`;

const ErrorMessage = styled.p`
  color: red;
  text-align: center;
`;

const ImagePdfDropzone: React.FC<ImagePdfDropzoneProps> = ({ onFileSelected, base64, width, height, uploadMessage }) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Detecta se a URL é um PDF pela extensão ou tipo MIME
  const isPdfUrl = (url: string): boolean => {
    if (!url) return false;
    try {
      // Remove query parameters
      const cleanUrl = url.split('?')[0].toLowerCase();
      return cleanUrl.endsWith('.pdf') || url.includes('pdf');
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (base64) {
      if (isPdfUrl(base64)) {
        // Se é uma URL de PDF, extrai o nome do arquivo
        const fileName = base64.split('/').pop()?.split('?')[0] || 'documento.pdf';
        setFileName(fileName);
        setSelectedImage(null);
      } else {
        // Se é uma imagem, renderiza normalmente
        setSelectedImage(base64);
        setFileName(null);
      }
    }
  }, [base64]);

  const convertBase64 = async (file: File): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);

      fileReader.onload = () => {
        resolve(fileReader.result as string);
      };

      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const validateImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.src = url;
  
      img.onload = () => {
        const { width, height } = img;
        URL.revokeObjectURL(url);
        resolve({ width, height });
      };
  
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Falha ao carregar a imagem.'));
      };
    });
  };

  const handleDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 1) {
      setError("Você só pode enviar um arquivo.");
      setSelectedImage(null);
      setFileName(null);
      onFileSelected(null);
    } else if (acceptedFiles.length === 1) {
      setError(null);
      const file = acceptedFiles[0];
      
      if (file.type === 'application/pdf') {
        setFileName(file.name);
        setSelectedImage(null);
        onFileSelected(file);
      } else {
        const base64 = await convertBase64(file);
        setSelectedImage(base64);
        setFileName(null);
        onFileSelected(file);
      }
    }
  }, [onFileSelected]);

  const handleChangeFile = () => {
    setSelectedImage(null);
    setFileName(null);
    setError(null);
    onFileSelected(null);
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'application/pdf': ['.pdf'],
    },
    onDrop: handleDrop,
  });

  return (
    <div>
      <DropContainer {...getRootProps({ isDragActive, isDragReject })}>
        <input {...getInputProps()} />
        {selectedImage ? (
          <ImageContainer>
            <UploadedImage src={selectedImage} width={width} height={height} />
            <ChangeImageButton onClick={handleChangeFile}>
              Alterar arquivo
            </ChangeImageButton>
          </ImageContainer>
        ) : fileName ? (
          <PdfIconContainer>
            <PdfIcon>
              <MdPictureAsPdf />
            </PdfIcon>
            <div>
              <p style={{ fontWeight: 'bold', margin: '0 0 8px 0' }}>Arquivo PDF selecionado:</p>
              <p style={{ wordBreak: 'break-all', margin: '0 0 12px 0' }}>{fileName}</p>
              <button 
                onClick={handleChangeFile} 
                style={{ 
                  cursor: 'pointer', 
                  padding: '8px 16px', 
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  fontSize: '14px'
                }}
              >
                Alterar arquivo
              </button>
            </div>
          </PdfIconContainer>
        ) : (
          <PdfIconContainer>
            <PdfIcon>
              <MdPictureAsPdf />
            </PdfIcon>
            <UploadMessage>
              {uploadMessage || "Arraste a imagem ou PDF aqui ou clique para selecionar."}
            </UploadMessage>
          </PdfIconContainer>
        )}
      </DropContainer>
      {error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  );
};

export { ImagePdfDropzone };
